/**
 * El texto sobre un conector, en el DOM y por encima del canvas.
 *
 * Mismo razonamiento que `GroupLabels`: la línea se dibuja en Pixi por debajo de
 * las tarjetas, pero su etiqueta tiene que leerse por encima de todo y con el
 * texto nítido del navegador. Son dos capas distintas, así que no pueden ser el
 * mismo objeto.
 *
 * Los elementos son `pointer-events: none` a propósito: el click tiene que
 * seguir llegando al canvas para poder arrastrar lo que haya debajo. El
 * hit-test lo hace `hit()` contra el rectángulo medido del elemento.
 */

export interface EdgeLabelItem {
  id: string;
  /** Punto medio de la arista, en coordenadas de MUNDO. */
  x: number;
  y: number;
  text: string;
  /** Color de la arista, ya resuelto a un entero 0xRRGGBB. */
  color: number;
  selected: boolean;
  /** Se oculta mientras se edita: ahí manda el input. */
  hidden: boolean;
}

interface LabelEl {
  el: HTMLElement;
  left: number;
  top: number;
}

export class EdgeLabels {
  readonly root: HTMLElement;
  private els = new Map<string, LabelEl>();

  constructor() {
    this.root = document.createElement("div");
    this.root.className = "edge-labels";
  }

  /** Crea, actualiza y descarta etiquetas para que reflejen el estado actual. */
  sync(
    items: EdgeLabelItem[],
    worldToScreen: (x: number, y: number) => { x: number; y: number },
  ): void {
    const live = new Set<string>();

    for (const item of items) {
      if (item.hidden || item.text.length === 0) continue;
      live.add(item.id);

      let entry = this.els.get(item.id);
      if (!entry) {
        const el = document.createElement("div");
        el.className = "edge-label";
        entry = { el, left: NaN, top: NaN };
        this.els.set(item.id, entry);
        this.root.appendChild(el);
      }
      const el = entry.el;

      if (el.textContent !== item.text) el.textContent = item.text;

      const hex = `#${item.color.toString(16).padStart(6, "0")}`;
      if (el.style.getPropertyValue("--edge-color") !== hex) {
        el.style.setProperty("--edge-color", hex);
      }
      el.classList.toggle("is-selected", item.selected);

      const p = worldToScreen(item.x, item.y);
      const left = Math.round(p.x);
      const top = Math.round(p.y);
      if (left !== entry.left || top !== entry.top) {
        entry.left = left;
        entry.top = top;
        el.style.left = `${left}px`;
        el.style.top = `${top}px`;
      }
    }

    for (const [id, entry] of this.els) {
      if (live.has(id)) continue;
      entry.el.remove();
      this.els.delete(id);
    }
  }

  /** ¿Cae este punto de pantalla sobre la etiqueta de esta arista? */
  hit(id: string, screenX: number, screenY: number): boolean {
    const el = this.els.get(id)?.el;
    if (!el) return false;
    // El CSS la centra sobre el punto medio con translate(-50%, -50%).
    const x = el.offsetLeft - el.offsetWidth / 2;
    const y = el.offsetTop - el.offsetHeight / 2;
    return (
      screenX >= x &&
      screenX <= x + el.offsetWidth &&
      screenY >= y &&
      screenY <= y + el.offsetHeight
    );
  }

  destroy(): void {
    for (const entry of this.els.values()) entry.el.remove();
    this.els.clear();
    this.root.remove();
  }
}
